import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, Check, Trash2, Lock } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import StarRating from '../ui/StarRating';
import Badge from '../ui/Badge';
import Countdown from '../ui/Countdown';
import { formatCurrency, discountPercent } from '../../utils/format';
import { getBookBadge } from '../../utils/badge';
import { useWishlistStore } from '../../context/useWishlistStore';
import { useCartStore } from '../../context/useCartStore';
import { useAuthStore } from '../../context/useAuthStore';
import { useState } from 'react';

export default function BookCard({ book, index = 0, showAddToCart = false, wishlistView = false, onRemoveFromWishlist, badgeOverride }) {
  const wishlisted = useWishlistStore((s) => s.bookIds.includes(book.id));
  const toggleWishlist = useWishlistStore((s) => s.toggle);
  const user = useAuthStore((s) => s.user);
  const addItem = useCartStore((s) => s.addItem);
  const inCart = useCartStore((s) => s.items.some((i) => i.book.id === book.id));
  const discount = discountPercent(book.price, book.mrp);
  // Early access availability outranks promotional badges — a locked or
  // early-unlocked book shows its access state, not "New Release" etc.
  const earlyAccess = book.earlyAccess;
  const earlyAccessLocked = Boolean(earlyAccess?.isEarlyAccess && !earlyAccess.canPurchase);
  const earlyAccessUnlocked = Boolean(earlyAccess?.isEarlyAccess && earlyAccess.canPurchase);
  const badge = earlyAccessLocked ? 'Upcoming' : earlyAccessUnlocked ? 'New Release' : (badgeOverride || getBookBadge(book));
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (inCart) return;
    addItem(book);
    toast.success(`Added "${book.title}" to cart`);
  };

  const handleRemoveFromWishlist = (e) => {
    e.preventDefault();
    onRemoveFromWishlist?.(book.id);
  };

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to use your wishlist');
      return;
    }
    const wasWishlisted = wishlisted;
    toggleWishlist(book.id);

    // Show heart animation only when adding to wishlist
    if (!wasWishlisted) {
      setShowHeartAnimation(true);
      // Hide the animation after it completes
      setTimeout(() => {
        setShowHeartAnimation(false);
      }, 1000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.3) }}
      className="group relative flex flex-col"
    >
      <Link to={`/book/${book.slug}`} className="block">
        <div
          className="relative aspect-[2/3] w-full overflow-hidden rounded-xl2 shadow-spine transition-transform duration-300 ease-out group-hover:-translate-y-1 group-hover:rotate-[-1.5deg] group-hover:shadow-soft-lg"
        >
          <img
            src={book.cover}
            alt={`Cover of ${book.title}`}
            loading="lazy"
            className="object-fill h-full w-full"
          />
          {/* spine highlight to sell the "physical book" feel */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-2 bg-gradient-to-r from-black/25 to-transparent" />
          {badge && (
            <div className="absolute top-1 left-2">
              <Badge size="sm">{badge}</Badge>
            </div>
          )}
          {/* Floating heart animation */}
          {showHeartAnimation && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Heart
                size={40}
                className="text-accent fill-accent animate-float-up"
              />
            </div>
          )}
          <button
            onClick={handleWishlistToggle}
            aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/85 backdrop-blur transition-all duration-200 hover:scale-110 hover:bg-white active:scale-95"
          >
            <Heart
              size={15}
              className={clsx(
                'transition-all duration-200',
                wishlisted || showHeartAnimation
                  ? 'scale-125 heart-filled'
                  : 'text-slate-500 hover:text-accent'
              )}
            />
          </button>
          {wishlistView && (
            <div className="absolute bottom-2 left-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              {earlyAccessLocked ? (
                <Link
                  to="/membership"
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium py-2 hover:bg-primary-700 transition-colors"
                >
                  <Lock size={14} /> Get Early Access
                </Link>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium py-2 hover:bg-primary-700 transition-colors"
                >
                  <ShoppingCart size={14} /> Add to Cart
                </button>
              )}
              <button
                onClick={handleRemoveFromWishlist}
                aria-label="Remove from wishlist"
                title="Remove from wishlist"
                className="flex items-center justify-center rounded-lg bg-white/90 dark:bg-surface-darkCard/90 border border-red-200 dark:border-red-500/30 text-red-500 p-2 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>
      </Link>

      <div className="mt-3 flex flex-col gap-1">
        <Link to={`/book/${book.slug}`}>
          <h3 className="line-clamp-1 font-display font-semibold text-[15px] text-slate-800 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-300 transition-colors">
            {book.title}
          </h3>
        </Link>
        {book.author ? (
          <Link to={`/authors/${book.author.slug}`} className="text-xs text-slate-500 dark:text-slate-400 hover:text-primary-600 line-clamp-1">
            {book.author.name}
          </Link>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500 line-clamp-1">
            {book.authorName || 'Unknown author'}
          </span>
        )}
        <StarRating rating={book.rating} reviewsCount={book.reviewsCount} />
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="font-mono font-semibold text-[15px] text-slate-800 dark:text-white">
            {formatCurrency(book.price)}
          </span>
          {discount > 0 && (
            <>
              <span className="font-mono text-xs text-slate-400 line-through">{formatCurrency(book.mrp)}</span>
              <span className="text-xs font-medium text-success">{discount}% off</span>
            </>
          )}
        </div>
        {earlyAccessLocked ? (
          <div className="mt-2 space-y-1">
            {earlyAccess.releaseDate && (
              <p className="text-[11px] text-slate-400 dark:text-slate-500">
                Available to everyone in <Countdown target={earlyAccess.releaseDate} className="font-mono" />
              </p>
            )}
            <Link
              to="/membership"
              className="flex items-center justify-center gap-1.5 rounded-lg bg-primary-50 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-100 dark:bg-white/5 dark:text-primary-300 dark:hover:bg-white/10"
            >
              <Lock size={13} /> Get Early Access
            </Link>
          </div>
        ) : showAddToCart && (
          <button
            onClick={handleAddToCart}
            className={clsx(
              'mt-2 flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-colors',
              inCart
                ? 'bg-success/10 text-success cursor-default'
                : 'bg-primary-50 text-primary-600 hover:bg-primary-100 dark:bg-white/5 dark:text-primary-300 dark:hover:bg-white/10'
            )}
          >
            {inCart ? <><Check size={13} /> In cart</> : <><ShoppingCart size={13} /> Add to cart</>}
          </button>
        )}
      </div>
    </motion.div>
  );
}
